import { format } from "date-fns"
import { ja } from "date-fns/locale"
import type { DateAfter, DateBefore, Matcher } from "react-day-picker"
import { Calendar as CalendarIcon, X } from "react-feather"

import { Button } from "@/components/ui/button/button"
import { Calendar } from "@/components/ui/calendar/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover/popover"
import { cn } from "@/lib/utils"

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: Matcher | Matcher[]
  fromDate?: Date
  toDate?: Date
  defaultMonth?: Date
  className?: string
  id?: string
}

export function DatePicker({
  value,
  onChange,
  placeholder = "日付を選択",
  disabled,
  fromDate,
  toDate,
  defaultMonth,
  className,
  id,
}: DatePickerProps) {
  const handleSelect = (date: Date | undefined) => {
    if (!date) {
      onChange?.(undefined)
      return
    }

    onChange?.(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12))
  }

  // Create disabled matcher based on fromDate and toDate
  let disabledMatcher: Matcher | Matcher[] | undefined = disabled

  if (fromDate || toDate) {
    const matchers: Matcher[] = []

    if (fromDate) {
      const beforeMatcher: DateBefore = { before: fromDate }
      matchers.push(beforeMatcher)
    }

    if (toDate) {
      const afterMatcher: DateAfter = { after: toDate }
      matchers.push(afterMatcher)
    }

    // Combine with existing disabled if provided
    if (disabled) {
      if (Array.isArray(disabled)) {
        disabledMatcher = [...disabled, ...matchers]
      } else {
        disabledMatcher = [disabled, ...matchers]
      }
    } else if (matchers.length === 1) {
      disabledMatcher = matchers[0]
    } else {
      disabledMatcher = matchers
    }
  }
  return (
    <Popover>
      <div className="relative flex items-center">
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              "h-10 w-full justify-start text-left font-normal border-slate-300 rounded-md px-3 text-sm text-slate-600",
              !value && "text-muted-foreground",
              value && "pr-8",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "yyyy年MM月dd日", { locale: ja }) : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        {value && (
          <button
            type="button"
            className="absolute right-2 rounded-sm opacity-50 hover:opacity-100 transition-opacity"
            aria-label="日付をクリア"
            onClick={(e) => {
              e.stopPropagation()
              onChange?.(undefined)
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          required
          selected={value}
          defaultMonth={defaultMonth ?? value ?? fromDate}
          onSelect={handleSelect}
          disabled={disabledMatcher}
          locale={ja}
        />
      </PopoverContent>
    </Popover>
  )
}
